import { ErrorHandler, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { IS_VIEW_ALL_ROUTE } from '../constants/menu-structure-route-data';
import { ModuleNumbers } from '../enums/module-numbers.enum';
import { MenuListLogicService, MenuModuleRoutes } from './menu-list-logic.service';

export type MenuNavigationOutcome = 'navigated' | 'same-target' | 'cancelled' | 'failed';

@Injectable({ providedIn: 'root' })
export class MenuStructureNavigationService {
  private activeTransaction: symbol | null = null;

  constructor(
    private readonly router: Router,
    private readonly errorHandler: ErrorHandler,
    private readonly menuListLogicService: MenuListLogicService
  ) {}

  getNodeUrl(module: ModuleNumbers, nodeId: string): string | null {
    const routes = this.menuListLogicService.getModuleRoutes(module);
    if (!routes?.selectedRoot) {
      return null;
    }
    const nodeUrl = routes.appendNodeId ? `${routes.selectedRoot}/${nodeId}` : routes.selectedRoot;
    const targetUrl = routes.defaultChildRoute ? `${nodeUrl}/${routes.defaultChildRoute}` : nodeUrl;
    return this.withPreservedQueryParams(targetUrl, routes);
  }

  getViewAllUrl(module: ModuleNumbers, selectedNodeId: string): string | null {
    const routes = this.menuListLogicService.getModuleRoutes(module);
    if (!routes?.selectedRoot || !routes.viewAllRoot) {
      return null;
    }
    const currentUrl = this.getCurrentPath();
    if (!selectedNodeId) {
      return this.withPreservedQueryParams(
        this.isViewAllRoute() ? currentUrl : routes.viewAllRoot,
        routes
      );
    }
    if (!routes.preserveNestedRoute) {
      return this.withPreservedQueryParams(routes.viewAllRoot, routes);
    }
    const selectedNodeUrl = `${routes.selectedRoot}/${selectedNodeId}`;
    const nestedRoute = currentUrl.startsWith(selectedNodeUrl)
      ? currentUrl.slice(selectedNodeUrl.length)
      : '';
    return this.withPreservedQueryParams(`${routes.viewAllRoot}${nestedRoute}`, routes);
  }

  async navigate(url: string): Promise<MenuNavigationOutcome> {
    try {
      const targetUrl = this.router.parseUrl(url);
      if (this.isCurrentUrl(targetUrl)) {
        return 'same-target';
      }
      return (await this.router.navigateByUrl(targetUrl)) ? 'navigated' : 'cancelled';
    } catch (error) {
      this.errorHandler.handleError(error);
      return 'failed';
    }
  }

  isCompleted(outcome: MenuNavigationOutcome): boolean {
    return outcome === 'navigated' || outcome === 'same-target';
  }

  startTransaction(): symbol | null {
    if (this.activeTransaction) {
      return null;
    }
    this.activeTransaction = Symbol('menu-navigation');
    return this.activeTransaction;
  }

  finishTransaction(transaction: symbol): void {
    if (this.activeTransaction === transaction) {
      this.activeTransaction = null;
    }
  }

  isViewAllRoute(): boolean {
    const activeRoute = this.getDeepestRoute(this.router.routerState.snapshot.root);
    return activeRoute.pathFromRoot.some((route) => route.data[IS_VIEW_ALL_ROUTE] === true);
  }

  private getCurrentPath(): string {
    return this.router.serializeUrl(this.router.parseUrl(this.router.url)).split(/[?#]/)[0];
  }

  private withPreservedQueryParams(url: string, routes: MenuModuleRoutes): string {
    if (!routes.preservedQueryParams?.length) {
      return url;
    }
    const targetUrl = this.router.parseUrl(url);
    const currentUrl = this.router.parseUrl(this.router.url);
    routes.preservedQueryParams.forEach((key) => {
      if (currentUrl.queryParams[key] !== undefined) {
        targetUrl.queryParams[key] = currentUrl.queryParams[key];
      }
    });
    return this.router.serializeUrl(targetUrl);
  }

  private isCurrentUrl(targetUrl: UrlTree): boolean {
    const currentUrl = this.router.parseUrl(this.router.url);
    return this.router.serializeUrl(targetUrl) === this.router.serializeUrl(currentUrl);
  }

  private getDeepestRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let deepestRoute = route;
    while (deepestRoute.firstChild) {
      deepestRoute = deepestRoute.firstChild;
    }
    return deepestRoute;
  }
}
